import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import MyAccountSideBar from "./MyAccountSideBar";
import { getOrders } from "../services/authenticatedRequests";
import { S3_URL_PRODUCTS } from "../utils/constants";
import { Link } from "react-router-dom";
import { trackOrder } from "../services/nonAuthRequets";
import jsPDF from "jspdf";
import { logoBase64Invoice } from "../utils/constants";
import "../assets/css/order-page.css"; // Import your CSS file for styles
import LoadingComponent from "./LoadingComponent";
import NoResultsComponent from "./NoResultsComponent";
const MyOrders = () => {
  document.title = "My Orders | Bigmall";
  const [active, setActive] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
  // New filter state variables
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState("");

  // Toggle sidebar visibility for mobile view
  const sidebarController = () => {
    setActive(!active);
  };

  // Helper to trim product name if over 15 characters
  const trimProductName = (name) => {
    return name.length > 15 ? name.substring(0, 15) + "..." : name;
  };

  // Fetch orders from API with optional filters
  const fetchOrders = async (page = 0) => {
    setIsLoading(true);
    try {
      const response = await getOrders(
        page,
        5,
        selectedStatus,
        selectedDateFilter
      );
      setOrders(response.content);
      setPageCount(response.totalPages);
      setCurrentPage(response.number);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  // Handle date filter change
  const handleDateFilterChange = (e) => {
    setSelectedDateFilter(e.target.value);
  };

  // Re-fetch orders whenever filters change
  useEffect(() => {
    fetchOrders(0); // Reset to first page when filters change
  }, [selectedStatus, selectedDateFilter]);

  // Handle pagination click
  const handlePageClick = (data) => {
    fetchOrders(data.selected);
  };

  const handleDownloadInvoice = async (orderId) => {
    setLoadingInvoiceId(orderId);
    try {
      //setLoading(true);
      const response = await trackOrder(orderId);
      //console.log("Order details:", response);
      //setLoading(false);

      // Initialize jsPDF with A4 dimensions (portrait)
      const doc = new jsPDF("p", "mm", "a4");
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = margin;

      // Helper: Check if there is enough space; if not, add a new page.
      const checkPageBreak = (neededSpace = 10) => {
        if (y + neededSpace > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      // Helper: Add text that may span multiple lines, handling wrapping and page breaks.
      // Returns the updated y position.
      const addWrappedText = (text, x, maxWidth, lineHeight = 6) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line) => {
          checkPageBreak(lineHeight);
          doc.text(line, x, y);
          y += lineHeight;
        });
        return y;
      };

      // === Invoice Header ===
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      checkPageBreak(15);
      // Move the logo to the top-right corner.
      const logoWidth = 50;
      const logoHeight = 15;
      const logoX = pageWidth - margin - logoWidth;
      doc.addImage(logoBase64Invoice, "PNG", logoX, y, logoWidth, logoHeight);
      // Place the "Invoice" title at the left.
      doc.text("Invoice", margin, y + logoHeight);
      y += logoHeight + 10;

      // === Order Basic Details ===
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      checkPageBreak(30);
      doc.text(`Order Number: ${response.orderNumber}`, margin, y);
      y += 6;
      doc.text(
        `Date: ${new Date(response.createdAt).toLocaleDateString()}`,
        margin,
        y
      );
      y += 6;
      doc.text(`Payment Method: ${response.paymentMethod}`, margin, y);
      y += 6;
      doc.text(`Status: ${response.status}`, margin, y);
      y += 10;

      // === Billing and Shipping Information (Side by Side) ===
      checkPageBreak(40);
      // Calculate column widths for two side-by-side blocks.
      const columnWidth = (pageWidth - 2 * margin) / 2;
      const billingX = margin;
      const shippingX = margin + columnWidth;
      let billY = y;
      let shipY = y;

      // Headers for each column.
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Billing Information", billingX, billY);
      doc.text("Shipping Information", shippingX, shipY);
      billY += 6;
      shipY += 6;

      // Billing details.
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Name: ${response.billingFirstName} ${response.billingLastName}`,
        billingX,
        billY
      );
      billY += 6;
      doc.text(`Email: ${response.billingEmail}`, billingX, billY);
      billY += 6;
      doc.text(`Phone: ${response.billingPhone}`, billingX, billY);
      billY += 6;
      const billingAddress = `Address: ${response.billingAddress1}, ${response.billingCity}, ${response.billingState}, ${response.billingPostcode}, ${response.billingCountry}`;
      const billingAddressLines = doc.splitTextToSize(
        billingAddress,
        columnWidth
      );
      billingAddressLines.forEach((line) => {
        checkPageBreak(6);
        doc.text(line, billingX, billY);
        billY += 6;
      });

      // Shipping details.
      doc.text(
        `Name: ${response.shippingFirstName} ${response.shippingLastName}`,
        shippingX,
        shipY
      );
      shipY += 6;
      doc.text(`Email: ${response.shippingEmail}`, shippingX, shipY);
      shipY += 6;
      doc.text(`Phone: ${response.shippingPhone}`, shippingX, shipY);
      shipY += 6;
      const shippingAddress = `Address: ${response.shippingAddress1}, ${response.shippingCity}, ${response.shippingState}, ${response.shippingPostcode}, ${response.shippingCountry}`;
      const shippingAddressLines = doc.splitTextToSize(
        shippingAddress,
        columnWidth
      );
      shippingAddressLines.forEach((line) => {
        checkPageBreak(6);
        doc.text(line, shippingX, shipY);
        shipY += 6;
      });

      // Move y to the lower end of the two columns.
      y = Math.max(billY, shipY) + 4;

      // === Order Items Table with Borders ===
      checkPageBreak(20);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const tableX = margin;
      let tableY = y;
      const tableWidth = pageWidth - 2 * margin;
      const col1Width = tableWidth * 0.6; // Name column
      const col2Width = tableWidth * 0.2; // Quantity column
      const col3Width = tableWidth * 0.2; // Price column

      // Draw table header with borders.
      const headerHeight = 6;
      // Header texts with a bit of padding.
      doc.text("Name", tableX + 2, tableY + 4);
      doc.text("Quantity", tableX + col1Width + 2, tableY + 4);
      doc.text("Price", tableX + col1Width + col2Width + 2, tableY + 4);
      // Draw header rectangle border.
      doc.rect(tableX, tableY, tableWidth, headerHeight);
      // Draw vertical lines in header.
      doc.line(
        tableX + col1Width,
        tableY,
        tableX + col1Width,
        tableY + headerHeight
      );
      doc.line(
        tableX + col1Width + col2Width,
        tableY,
        tableX + col1Width + col2Width,
        tableY + headerHeight
      );
      tableY += headerHeight;

      doc.setFont("helvetica", "normal");
      // Render each product as a table row with borders.
      response.products.forEach((product) => {
        // Wrap the product name if needed, subtracting padding.
        const nameLines = doc.splitTextToSize(
          product.productName,
          col1Width - 4
        );
        const rowHeight = Math.max(nameLines.length * 6, 6) + 2; // adding vertical padding
        checkPageBreak(rowHeight);
        const rowY = tableY;

        // Draw cell border for the row.
        doc.rect(tableX, rowY, tableWidth, rowHeight);
        // Draw vertical dividing lines.
        doc.line(
          tableX + col1Width,
          rowY,
          tableX + col1Width,
          rowY + rowHeight
        );
        doc.line(
          tableX + col1Width + col2Width,
          rowY,
          tableX + col1Width + col2Width,
          rowY + rowHeight
        );

        // Print product name (with padding).
        let currentLineY = rowY + 4;
        nameLines.forEach((line) => {
          doc.text(line, tableX + 2, currentLineY);
          currentLineY += 6;
        });
        // Print quantity and price in their cells.
        doc.text(product.quantity.toString(), tableX + col1Width + 2, rowY + 4);
        doc.text(
          "LKR " + product.price.toFixed(2),
          tableX + col1Width + col2Width + 2,
          rowY + 4
        );

        tableY += rowHeight;
      });
      y = tableY + 4;

      // Define two X positions near the right side for label & price
      const summaryLabelX = pageWidth - margin - 50; // Adjust "50" as needed
      const summaryPriceX = pageWidth - margin;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      // Subtotal
      doc.text("Subtotal:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.totalAmount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Delivery Fee
      doc.text("Delivery Fee:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.deliveryFee.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Discount
      doc.text("Discount:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderDiscount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Tax
      doc.text("Tax:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderTax.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      y += 6;

      // Bold the Total row
      doc.setFont("helvetica", "bold");
      doc.text("Total:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.netAmount.toFixed(2)}`, summaryPriceX, y, {
        align: "right",
      });
      doc.setFont("helvetica", "normal");
      y += 6;

      // Draw a horizontal line separator
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      // Footer Section (printed right below the summary)
      doc.text("https://bigmall.lk/", margin, y);
      y += 6;
      doc.text("Email: info@bigmall.lk", margin, y);
      y += 6;
      doc.text("Tel: (+94) 777961772 / (+94) 741337427", margin, y);

      const timestamp = new Date().getTime();
      const fileName = `invoice_${orderId}_${timestamp}.pdf`;
      doc.output("dataurlnewwindow");
    } catch (error) {
      console.error(
        "Error fetching order details or generating invoice:",
        error
      );
     
    }
    finally {
      setLoadingInvoiceId(null);
    }
  };

  return (
    <section className="vendor-two-details py-20">
      {/* Overlay for mobile toggling */}
      <div
        className={`${active ? "show" : ""}`}
        onClick={sidebarController}
      ></div>
      <div className="container container-lg">
        <div className="vendor-two-details-wrapper d-flex flex-wrap align-items-start gap-24">
          <MyAccountSideBar
            active={active}
            sidebarController={sidebarController}
          />
          <div className="vendor-two-details__contents flex-grow-1">
            <div className="bg-white rounded-8 p-24">
              <div className="d-flex justify-content-between align-items-center mb-24">
                <h4 className="mb-0">My Orders</h4>
                <button
                  onClick={sidebarController}
                  type="button"
                  className="shop-sidebar__toggle d-lg-none d-flex w-40 h-40 flex-center border border-gray-200 rounded-circle hover-bg-gray-50"
                >
                  <i className="ph ph-list" />
                </button>
              </div>
              {/* Filter Controls */}
             
             {!isLoading && orders.length > 0 && (
              <>
              <div className="d-none d-md-flex gap-3 mb-10 justify-content-between w-25 ">
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                  <option value="to-ship">To Ship</option>
                  <option value="PROCESSING">Processing</option>
                </select>
                <select
                  className="form-select"
                  value={selectedDateFilter}
                  onChange={handleDateFilterChange}
                >
                  <option value="">All Dates</option>
                  <option value="LAST_5_ORDERS">Last 5 Orders</option>
                  <option value="LAST_15_DAYS">Last 15 Days</option>
                  <option value="LAST_30_DAYS">Last 30 Days</option>
                  <option value="LAST_6_MONTHS">Last 6 Months</option>
                  <option value="CURRENT_YEAR">Current Year</option>
                </select>
              </div>
              <div className="d-flex d-md-none gap-3 mb-10 justify-content-between w-75 ">
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                  <option value="to-ship">To Ship</option>
                  <option value="PROCESSING">Processing</option>
                </select>
                <select
                  className="form-select"
                  value={selectedDateFilter}
                  onChange={handleDateFilterChange}
                >
                  <option value="">All Dates</option>
                  <option value="LAST_5_ORDERS">Last 5 Orders</option>
                  <option value="LAST_15_DAYS">Last 15 Days</option>
                  <option value="LAST_30_DAYS">Last 30 Days</option>
                  <option value="LAST_6_MONTHS">Last 6 Months</option>
                  <option value="CURRENT_YEAR">Current Year</option>
                </select>
             
            
              </div>
              </>
              )}
              {isLoading ? (
                <LoadingComponent message="Loading orders..." />
              ) : orders.length === 0 ? (
                <NoResultsComponent message="No orders found." />
              ) : (
                orders.map((order) => (
                  <Link
                    to={`/orderdetails/${order.orderDBid}`}
                    key={order.orderDBid}
                    className="text-decoration-none text-reset d-flex"
                  >
                    <div
                      className="card mb-10 order-card"
                      style={{
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* Top Section: Tracking Number & Status */}
                      <div className="p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-muted small">
                            Tracking Number: {order.orderTrackingNumber}
                          </div>
                          <div>
                            <span className="badge bg-secondary">
                              {order.orderStatus
                                ? order.orderStatus.toUpperCase()
                                : "PENDING"}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              // Stop the event from propagating to the Link
                              e.preventDefault();
                              e.stopPropagation();
                              handleDownloadInvoice(order.orderTrackingNumber);
                            }}
                           className="btn btn-outline-main invoice-btn"
                           
                            disabled={loadingInvoiceId === order.orderTrackingNumber}
                          >
                            {loadingInvoiceId === order.orderTrackingNumber ? "Loading..." : "View Invoice"}
                          </button>
                        </div>
                        <hr />
                      </div>
                      {/* Order Items rendered vertically */}
                      <div className="card-body p-2">
                        {order.basicProductResponseDTOList.map((product) => (
                          <div
                            key={product.productId}
                            className="row align-items-center mb-3"
                          >
                            <div className="col-12 col-sm-auto">
                              <img
                                src={`${S3_URL_PRODUCTS}${product.productImages[0]}`}
                                alt={product.productName}
                                className="img-fluid"
                                style={{
                                  width: "64px",
                                  height: "64px",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                            <div
                              className="col-auto"
                              style={{ width: "150px" }}
                            >
                              <p className="mb-0 text-muted">
                                {trimProductName(product.productName)}
                              </p>
                            </div>

                            <div className="col text-center">
                              <p className="mb-0 text-muted">
                                Qty: {product.lineItemsDTOS[0].lineItemQuantity}
                              </p>
                            </div>
                            <div className="col text-end">
                              <p className="mb-0 text-muted">
                                LKR{" "}
                                {product.lineItemsDTOS[0].lineItemPrice.toFixed(
                                  2
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
        {pageCount > 1 && (
          <div className="container">
            {/* <div className="row justify-content-center mt-4"> */}
            <ReactPaginate
              previousLabel={"Previous"}
              nextLabel={"Next"}
              breakLabel={"..."}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName={
                "pagination pagination-sm justify-content-center"
              }
              pageClassName={"page-item"}
              pageLinkClassName={"page-link"}
              previousClassName={"page-item"}
              previousLinkClassName={"page-link"}
              nextClassName={"page-item"}
              nextLinkClassName={"page-link"}
              activeClassName={"active"}
              breakClassName={"page-item"}
              breakLinkClassName={"page-link"}
              forcePage={currentPage}
            />
            {/* </div> */}
          </div>
        )}
      </div>
    </section>
  );
};

export default MyOrders;
